import { Vec3, sub, length, add } from '../math/vec3'
import { clamp } from '../math/scalar'
import { CelestialBody } from '../world/body'
import { SYSTEM } from '../world/system'
import { OrbitElements, stateFromElements, semiLatusRectum, isClosed, orbitalPeriod, periapsisRadius, apoapsisRadius } from './elements'
import { propagate, trueToMean, meanMotion } from './kepler'
import { bodyStateAt } from './ephemeris'

const TAU = Math.PI * 2
const wrap = (a: number): number => ((a % TAU) + TAU) % TAU

export type TipoEvento = 'periapside' | 'apoapside' | 'impatto' | 'atmosfera' | 'entra-soi' | 'esce-soi'

export interface Evento {
  readonly tipo: TipoEvento
  /** fra quanti secondi */
  readonly quando: number
  readonly dove: Vec3
  readonly corpo?: string
}

/** Punti per disegnare l'orbita. Per le coniche aperte si taglia al limite dato. */
export function campionaConica(el: OrbitElements, punti = 180, raggioMassimo = Infinity): Vec3[] {
  const p = semiLatusRectum(el)
  const out: Vec3[] = []

  if (isClosed(el)) {
    for (let i = 0; i <= punti; i++) {
      out.push(stateFromElements({ ...el, trueAnomaly: (i / punti) * TAU }).position)
    }
    return out
  }

  // iperbole: l'anomalia vera vive fra ±acos(−1/e), gli asintoti
  const limite = Math.acos(clamp(-1 / el.ecc, -1, 1)) * 0.995
  for (let i = 0; i <= punti; i++) {
    const nu = -limite + (i / punti) * 2 * limite
    const r = p / (1 + el.ecc * Math.cos(nu))
    if (r > raggioMassimo || r < 0) continue
    out.push(stateFromElements({ ...el, trueAnomaly: nu }).position)
  }
  return out
}

/** Quanto tempo manca perche l'orbita raggiunga una data anomalia vera. */
export function tempoAAnomalia(el: OrbitElements, nu: number): number {
  const n = meanMotion(el)
  const M0 = trueToMean(el.trueAnomaly, el.ecc)
  const M1 = trueToMean(nu, el.ecc)
  if (isClosed(el)) return wrap(M1 - M0) / n
  return (M1 - M0) / n
}

/**
 * Come tempoAAnomalia, ma sempre STRETTAMENTE positivo su un'orbita chiusa.
 * Se si e gia sul punto chiesto — come capita al periapside subito dopo
 * un'accensione — l'occorrenza che interessa e quella del giro dopo, non
 * quella di adesso: altrimenti il periapside sparirebbe dalla lista proprio
 * quando ci si e sopra.
 */
export function tempoAlProssimo(el: OrbitElements, nu: number): number {
  const t = tempoAAnomalia(el, nu)
  if (!isClosed(el)) return t
  return t > 1e-6 ? t : t + orbitalPeriod(el)
}

/** Le due anomalie vere a cui l'orbita passa per un dato raggio, se esistono. */
export function anomaliePerRaggio(el: OrbitElements, r: number): number[] {
  const p = semiLatusRectum(el)
  if (el.ecc < 1e-12) return Math.abs(p - r) < 1 ? [0] : []
  const cos = (p / r - 1) / el.ecc
  if (cos < -1 || cos > 1) return []
  const nu = Math.acos(clamp(cos, -1, 1))
  return [nu, TAU - nu]
}

function primoTempoPositivo(el: OrbitElements, raggio: number): number | null {
  const tempi = anomaliePerRaggio(el, raggio)
    .map((nu) => tempoAAnomalia(el, nu))
    .filter((t) => t > 1e-6 && Number.isFinite(t))
    .sort((a, b) => a - b)
  return tempi[0] ?? null
}

/**
 * Gli eventi che aspettano il veicolo lungo la traiettoria.
 *
 * Gli apsidi e gli incontri col suolo si trovano analiticamente, perche la
 * conica li conosce. Gli ingressi in sfera d'influenza no: la luna si muove
 * mentre il veicolo viaggia, e i due si inseguono. Quelli si trovano
 * campionando e poi affinando per bisezione.
 */
export function prossimiEventi(
  el: OrbitElements, body: CelestialBody, t: number, orizzonte: number,
): Evento[] {
  const eventi: Evento[] = []
  const dove = (dt: number): Vec3 => stateFromElements(propagate(el, dt)).position

  const aggiungi = (tipo: TipoEvento, quando: number | null, corpo?: string): void => {
    if (quando === null || quando <= 0 || quando > orizzonte) return
    eventi.push(corpo ? { tipo, quando, dove: dove(quando), corpo } : { tipo, quando, dove: dove(quando) })
  }

  // ── incontri col corpo centrale, che vengono prima di tutto il resto
  const impatto = periapsisRadius(el) < body.radius ? primoTempoPositivo(el, body.radius) : null
  const tetto = body.atmosphere ? body.radius + body.atmosphere.ceiling : null
  const atmosfera = tetto !== null && periapsisRadius(el) < tetto ? primoTempoPositivo(el, tetto) : null

  aggiungi('impatto', impatto)
  aggiungi('atmosfera', atmosfera)

  // ── apsidi, ma solo se ci si arriva prima di schiantarsi
  const limite = impatto ?? atmosfera ?? Infinity
  const tPe = tempoAlProssimo(el, 0)
  if (tPe > 0 && tPe < limite) aggiungi('periapside', tPe)
  if (isClosed(el)) {
    const tAp = tempoAlProssimo(el, Math.PI)
    if (tAp > 0 && tAp < limite) aggiungi('apoapside', tAp)
  }

  // ── uscita dalla sfera d'influenza del corpo centrale
  if (Number.isFinite(body.soiRadius)) {
    const apo = apoapsisRadius(el)
    if (apo > body.soiRadius) aggiungi('esce-soi', primoTempoPositivo(el, body.soiRadius), body.parentId ?? undefined)
  }

  // ── ingressi nelle sfere dei figli: qui si campiona, perche la luna si muove
  for (const figlio of Object.values(SYSTEM)) {
    if (figlio.parentId !== body.id) continue
    const passi = 400
    const passo = Math.min(orizzonte, limite) / passi
    let precedente = Infinity
    for (let i = 1; i <= passi; i++) {
      const dt = i * passo
      const d = length(sub(dove(dt), bodyStateAt(figlio, t + dt).position))
      if (d < figlio.soiRadius && precedente >= figlio.soiRadius) {
        // bisezione fra i due campioni per trovare il confine
        let a = dt - passo
        let b = dt
        for (let k = 0; k < 40; k++) {
          const m = (a + b) / 2
          const dm = length(sub(dove(m), bodyStateAt(figlio, t + m).position))
          if (dm < figlio.soiRadius) b = m
          else a = m
        }
        aggiungi('entra-soi', b, figlio.id)
        break
      }
      precedente = d
    }
  }

  return eventi.sort((a, b) => a.quando - b.quando)
}
